import {
  GetApplicationResponse,
  PostApplicationRequestBody,
  PutApplicationRequestBody,
} from '@iwoca/lapi-client/edge';
import { PostApplicationRequestBody as PostApplicationRequestBodyDE } from '@iwoca/lapi-client/edge_de';
import { currencyToNumber } from '@iwoca/orion';

import { isPartnerAffiliateTag } from './isPartnerAffiliateTag';
import { TReferralSession } from './referralSession';
import { TGetStartedDataDE } from '../components/GetStarted/GetStartedDE/GetStartedDE';
import { TGetStartedDataUK } from '../components/GetStarted/GetStartedUK/GetStartedUK';
import { TRecursiveNonNullable, TSupportedLocales } from '../types';

export const getInitialApplicationBodyUK = (
  affiliateTag: string,
  amount?: TGetStartedDataUK['amount'],
  loanDuration?: TGetStartedDataUK['loanDuration'],
  loanPurpose?: TGetStartedDataUK['loanPurpose'],
): PostApplicationRequestBody => {
  const applicationBody: PostApplicationRequestBody = {
    data: {
      requested_products: [
        {
          amount: amount
            ? currencyToNumber(amount, {
                currencySymbol: true,
                locale: 'en-GB',
                minorUnits: false,
              })
            : undefined,
          purpose: loanPurpose,
          duration: loanDuration ? getDurationValueUK(loanDuration) : undefined,
          qualitative_duration: loanDuration
            ? durationLabelUK[loanDuration]
            : undefined,
        },
      ],
      application_source: 'SIGNUP_FRONTEND',
    },
  };

  if (isPartnerAffiliateTag(affiliateTag)) {
    applicationBody.data!.introducer = { affiliate_tag: affiliateTag };
  }

  return applicationBody;
};

export const getInitialApplicationBodyDE = (
  affiliateTag: string,
  {
    amount,
    loanPurpose,
    loanDuration,
    loanPurposeDetails,
  }: Partial<Omit<TGetStartedDataDE, 'email'>>,
  referralSession?: TReferralSession | null,
): PostApplicationRequestBodyDE => {
  const applicationBody: PostApplicationRequestBodyDE = {
    data: {
      requested_products: [
        {
          product_type: 'credit_facility',
          amount: amount
            ? currencyToNumber(amount, {
                currencySymbol: true,
                locale: 'de-DE',
                minorUnits: false,
              })
            : undefined,
          purpose: loanPurpose,
          duration: getDurationValueDE(loanDuration),
          qualitative_duration: loanDuration,
          detailed_purpose: loanPurposeDetails || '',
        },
      ],
      ...(referralSession && {
        referral_code: referralSession.referral_code,
        referral_session_id: referralSession.referral_session_id,
      }),
    },
  };

  if (isPartnerAffiliateTag(affiliateTag)) {
    applicationBody.data!.introducer = { affiliate_tag: affiliateTag };
  }

  return applicationBody;
};

export const getUpdatedApplicationBody = (
  application: GetApplicationResponse['data'],
  {
    amount,
    loanPurpose,
    loanDuration,
    loanPurposeDetails,
  }: Partial<TGetStartedDataUK | TGetStartedDataDE>,
  locale: TSupportedLocales,
): PutApplicationRequestBody => ({
  data: {
    requested_products: [
      {
        amount: amount
          ? currencyToNumber(amount, {
              currencySymbol: true,
              locale: locale === 'de' ? 'de-DE' : locale,
              minorUnits: false,
            })
          : undefined,
        product_type:
          application?.requested_products?.[0]?.product_type || undefined,
        duration:
          getDurationValueForLocale(loanDuration, locale) ||
          application?.requested_products?.[0]?.duration ||
          undefined,
        purpose:
          loanPurpose ||
          application?.requested_products?.[0]?.purpose ||
          (amount ? 'other' : undefined),
        detailed_purpose:
          loanPurposeDetails ||
          application?.requested_products?.[0]?.detailed_purpose ||
          undefined,
        qualitative_duration:
          getQualitativeDurationForLocale(loanDuration, locale) ||
          application?.requested_products?.[0]?.qualitative_duration ||
          undefined,
      },
    ],
  },
});

const getDurationValueForLocale = (
  value:
    | TGetStartedDataUK['loanDuration']
    | TGetStartedDataDE['loanDuration']
    | undefined,
  locale: TSupportedLocales,
) => {
  if (!value) return;

  return locale === 'de'
    ? getDurationValueDE(value as TGetStartedDataDE['loanDuration'])
    : getDurationValueUK(value as TGetStartedDataUK['loanDuration']);
};

const getQualitativeDurationForLocale = (
  value:
    | TGetStartedDataUK['loanDuration']
    | TGetStartedDataDE['loanDuration']
    | undefined,
  locale: TSupportedLocales,
) => {
  if (!value) return;

  return locale === 'de'
    ? (value as TGetStartedDataDE['loanDuration'])
    : durationLabelUK[value];
};

const getDurationValueUK = (
  value: TGetStartedDataUK['loanDuration'],
): TRecursiveNonNullable<PostApplicationRequestBody>['data']['requested_products'][number]['duration'] => {
  switch (value) {
    case '12_months':
      return {
        amount: 12,
        unit: 'months',
      };
    case '24_months':
      return {
        amount: 24,
        unit: 'months',
      };
    case '36_months':
      return {
        amount: 36,
        unit: 'months',
      };
    case '48_months':
      return {
        amount: 48,
        unit: 'months',
      };
    case '60_months':
      return {
        amount: 60,
        unit: 'months',
      };
  }
};

type TDurationLabelUK = {
  [
    key: string
  ]: TRecursiveNonNullable<PostApplicationRequestBody>['data']['requested_products'][number]['qualitative_duration'];
};
const durationLabelUK: TDurationLabelUK = {
  '12_months': 'few_months',
  '24_months': 'one_to_two_years',
  '36_months': 'three_plus_years',
  '48_months': 'ongoing',
  '60_months': 'unsure',
};

const getDurationValueDE = (
  value: TGetStartedDataDE['loanDuration'] | undefined,
):
  | TRecursiveNonNullable<PostApplicationRequestBody>['data']['requested_products'][number]['duration']
  | undefined => {
  if (!value) return;

  switch (value) {
    case 'few_months':
    case 'ongoing':
      return { amount: 12, unit: 'months' };
    case 'one_to_two_years':
      return { amount: 24, unit: 'months' };
    case 'three_plus_years':
      return { amount: 60, unit: 'months' };
  }
};
